import type { Document } from "@elation/api-client/src/stitch-web/models/document";
import type { UnmatchedPatientUpload } from "@elation/api-client/src/stitch-web/models/unmatchedPatientUpload";

import { parseNullableDate } from "@src/shared/utils/dates";

type MaybeDocument = Document | null | undefined;
type NeedsReviewIdentifierSummary = NonNullable<
  UnmatchedPatientUpload["identifierSummary"]
>;

const NEEDS_REVIEW_IDENTIFIER_LABELS: Record<
  keyof NeedsReviewIdentifierSummary,
  string
> = {
  hasAddress: "address",
  hasDob: "DOB",
  hasEmail: "email",
  hasFirstName: "first name",
  hasLastName: "last name",
  hasMiddleName: "middle name",
  hasPhone: "phone",
  hasPreferredName: "preferred name",
  hasSex: "sex",
  hasSsnLast4: "SSN last 4",
};

const uploadDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

function fileNameFromS3Key(s3Key: string): string | null {
  const lastSegment = s3Key.split("/").filter(Boolean).at(-1)?.trim();
  return lastSegment || null;
}

export const DocumentEntity = {
  displayFileName(document: MaybeDocument): string | null {
    if (!document) return null;
    return (
      document.fileName.trim() ||
      fileNameFromS3Key(document.s3Key) ||
      `Document ${document.id}`
    );
  },

  isPdf(
    document: Pick<Document, "fileName" | "mimeType"> | null | undefined,
  ): boolean {
    if (!document) return false;
    return (
      document.mimeType.toLowerCase() === "application/pdf" ||
      document.fileName.trim().toLowerCase().endsWith(".pdf")
    );
  },

  uploadedAt(document: MaybeDocument): Date | null {
    return parseNullableDate(document?.uploadedAt);
  },

  uploadedLabel(document: MaybeDocument): string | null {
    if (!document) return null;
    const uploadedAt = DocumentEntity.uploadedAt(document);
    if (!uploadedAt) return "Upload date unknown";

    return `Uploaded ${uploadDateFormatter.format(uploadedAt)}`;
  },

  canReprocess(document: MaybeDocument): boolean {
    if (!document) return false;
    return Boolean(document.patientId) && DocumentEntity.isPdf(document);
  },

  needsReviewReasonLabel(reason: UnmatchedPatientUpload["reason"]): string {
    switch (reason) {
      case "insufficient_identifiers":
        return "Missing required patient identifiers";
      case "no_dob_lastname_match":
        return "No patient matched DOB and last name";
      default:
        return reason satisfies never;
    }
  },

  needsReviewIdentifierSummaryLabel(
    summary: UnmatchedPatientUpload["identifierSummary"],
  ): string {
    if (!summary) return "Identifier details unavailable";

    const fields = (
      Object.keys(NEEDS_REVIEW_IDENTIFIER_LABELS) as Array<
        keyof NeedsReviewIdentifierSummary
      >
    )
      .filter((key) => summary[key])
      .map((key) => NEEDS_REVIEW_IDENTIFIER_LABELS[key]);

    if (fields.length === 0) return "No usable identifiers found";
    return `Found ${fields.join(", ")}`;
  },
} as const;
